import { useState } from "react";
import { Link } from "react-router";
import { useAdminSubmissions, useMySubmissions } from "../api/client";
import { SUBMISSION_STATUSES, type SubmissionStatus, type SubmissionSummary } from "../api/types";
import { ErrorBanner, SeverityPill } from "../components/ui";
import { WidePanel } from "../components/Workspace";

const STATUS: Record<SubmissionStatus, { text: string; tone: "good" | "warn" | "crit" | "accent" | "neutral" }> = {
  TRIAGING: { text: "Triaging", tone: "accent" },
  PASSED: { text: "Ready to raise", tone: "good" },
  FLAGGED: { text: "Not flagged", tone: "warn" },
  TICKET_QUEUED: { text: "Creating ticket", tone: "accent" },
  TICKET_CREATED: { text: "Ticket created", tone: "good" },
  TICKET_FAILED: { text: "Failed", tone: "crit" },
  CLOSED_NO_TICKET: { text: "Closed", tone: "neutral" },
};

const formatTime = (iso: string): string =>
  new Date(iso).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });

function Table({ rows, showCreator }: { rows: SubmissionSummary[]; showCreator?: boolean }) {
  if (rows.length === 0) {
    return <p className="record-note">Nothing here yet.</p>;
  }
  return (
    <div className="table-wrap">
      <table className="table">
        <thead>
          <tr>
            <th>Company</th>
            <th>Type</th>
            <th>Severity</th>
            {showCreator && <th>Raised by</th>}
            <th>Status</th>
            <th>Updated</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.id}>
              <td>
                <Link to={`/submissions/${row.id}`}>{row.companyName}</Link>
              </td>
              <td>{row.ticketType}</td>
              <td>
                <SeverityPill severity={row.severity} />
              </td>
              {showCreator && <td>{row.creatorEmail}</td>}
              <td>
                <span className={`status-pill status-${STATUS[row.status].tone}`}>{STATUS[row.status].text}</span>
              </td>
              <td>{formatTime(row.statusChangedAt)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function MyTickets() {
  const query = useMySubmissions();
  return (
    <WidePanel>
      <div className="phase-head">
        <h1>My tickets</h1>
        <p>
          Everything you've raised. <Link to="/new/eng">Raise an engineering ticket</Link>
        </p>
      </div>
      {query.isPending ? (
        <p className="record-note">Loading…</p>
      ) : query.isError ? (
        <ErrorBanner>We couldn't load your tickets.</ErrorBanner>
      ) : (
        <Table rows={query.data} />
      )}
    </WidePanel>
  );
}

export function AdminQueue() {
  const [status, setStatus] = useState<SubmissionStatus>("TICKET_FAILED");
  const query = useAdminSubmissions(status);
  return (
    <WidePanel>
      <div className="phase-head">
        <h1>All submissions</h1>
        <p>Filter by status to find anything that needs attention.</p>
      </div>
      <div className="filter-chips">
        {SUBMISSION_STATUSES.map((option) => (
          <button key={option} className={`sample-btn${option === status ? " active" : ""}`} onClick={() => setStatus(option)}>
            {STATUS[option].text}
          </button>
        ))}
      </div>
      {query.isPending ? (
        <p className="record-note">Loading…</p>
      ) : query.isError ? (
        <ErrorBanner>We couldn't load submissions.</ErrorBanner>
      ) : (
        <Table rows={query.data} showCreator />
      )}
    </WidePanel>
  );
}
